<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/modules/user/stores/userStore'
import { ERouteNames } from '@/router/ERouteNames'
import ReferralBonusTooltip from '@/components/referral-bonus-tooltip/ReferralBonusTooltip.vue'
import ShareReferralButton from '@/components/share-referral-button/ShareReferralButton.vue'
import LeaderboardRulesTooltip from '@/components/leaderboard-rules-tooltip/LeaderboardRulesTooltip.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const userStore = useUserStore()
const { t } = useI18n()

const isLoading = ref(false)

const currentUserIndex = computed(() => userStore.currentUserIndex)
const myPoints = computed(() => userStore.user?.referralCount ?? 0)

const top1Points = computed(() => {
  const top1 = userStore.leaderboard[0]
  return top1 ? top1.referralCount : 0
})

const remainingPoints = computed(() => {
  if (currentUserIndex.value === 0) return 0
  return top1Points.value + 1 - myPoints.value
})

const progressPct = computed(() => {
  if (top1Points.value === 0) return 0
  const target = top1Points.value + 1
  return Math.min((myPoints.value / target) * 100, 100)
})

const goToLeaderboard = () => {
  router.push({ name: ERouteNames.LEADERBOARD })
}

onMounted(async () => {
  if (!userStore.user?.id) {
    await userStore.getUserData()
  }
  
  isLoading.value = true
  await userStore.getReferralLeaderboard()
  isLoading.value = false
})
</script>

<template>
  <div class="promo-wrapper w-100 d-flex rounded-xl">
    <div class="promo-container w-100 pa-4 d-flex flex-column ga-2 bg-background rounded-xl">
      <div class="d-flex align-center ga-2 justify-space-between">
        <div class="text-subtitle-1 cursor-default font-weight-medium">{{ t('promo.title') }}</div>
        <ReferralBonusTooltip />
      </div>

      <!-- Карточки статистики -->
      <div class="d-flex align-center justify-space-evenly">
        <div
          class="promo-stat-card d-flex flex-column align-center bg-background-extra rounded-pill overflow-hidden icon-user"
        >
          <div class="text-caption mt-2 cursor-default">{{ t('promo.invited') }}</div>
          <div class="text-subtitle-1 mb-2">
            {{ userStore.user?.referralCount }}
          </div>
        </div>

        <div class="promo-arrow-icon" />

        <div
          class="promo-stat-card d-flex flex-column align-center bg-background-extra rounded-pill overflow-hidden icon-gift"
        >
          <div class="text-caption mt-2 cursor-default font-weight-regular">{{ t('promo.earnedDays') }}</div>
          <div class="text-subtitle-1 mb-2">
            {{ userStore.user?.referralBonusDaysCount }}
          </div>
        </div>
      </div>

      <!-- Прогресс-бар до победы или мотивирующий текст -->
      <div v-if="!isLoading" class="d-flex flex-column ga-1 mt-2 w-100">
        <template v-if="currentUserIndex !== -1">
          <div class="progress-bar-track w-100 rounded overflow-hidden">
            <div 
              class="progress-bar-fill h-100 bg-success rounded" 
              :style="{ width: `${progressPct}%` }"
            ></div>
          </div>
          <div class="text-caption text-center font-weight-medium text-medium-emphasis mt-1">
            <span v-if="currentUserIndex > 0">
              {{ t('leaderboard.toFirstPlaceRemaining', { count: remainingPoints }) }}
            </span>
            <span v-else-if="currentUserIndex === 0" class="text-success font-weight-bold">
              {{ t('leaderboard.youLead') }}
            </span>
          </div>
        </template>
        <template v-else>
          <div class="text-caption text-center font-weight-medium text-medium-emphasis mt-1">
            {{ t('leaderboard.checkRulesPre') }}
            <LeaderboardRulesTooltip>
              <template #activator="{ props: rulesProps }">
                <span v-bind="rulesProps" class="text-info text-decoration-underline cursor-pointer font-weight-bold">
                  {{ t('leaderboard.contestRules') }}
                </span>
              </template>
            </LeaderboardRulesTooltip>
            {{ t('leaderboard.checkRulesPost') }}
          </div>
        </template>
      </div>

      <!-- Кнопки действий -->
      <div class="d-flex flex-column ga-2 mt-2">
        <v-btn
          color="secondary"
          variant="outlined"
          rounded="lg"
          block
          prepend-icon="ri-trophy-line"
          class="font-weight-medium"
          @click="goToLeaderboard"
        >
          {{ t('profile.viewLeaders') }}
        </v-btn>
        <share-referral-button />
      </div>
    </div>
  </div>
</template>

<style scoped>
.promo-wrapper {
  position: relative;
}

.promo-wrapper::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.5px;
  background: linear-gradient(
    135deg,
    rgb(var(--v-theme-indigo-glow)),
    rgb(var(--v-theme-purple-glow)),
    rgb(var(--v-theme-emerald-glow)),
    rgb(var(--v-theme-blue-glow)),
    rgb(var(--v-theme-indigo-glow))
  );
  background-size: 300% 300%;
  animation: aurora-border 8s ease infinite;
  
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  
  pointer-events: none;
  z-index: 2;
}

@keyframes aurora-border {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.promo-container {
  position: relative;
  z-index: 1;
  border: none !important;
}

.promo-stat-card {
  position: relative;
  min-width: 128px;
}

.promo-arrow-icon {
  width: 24px;
  height: 43px;
  background: url('@/assets/icons/arrow-right.svg') no-repeat center;
}

.icon-user::before,
.icon-gift::before {
  content: '';
  position: absolute;
  top: 25px;
  left: 4px;
  width: 32px;
  height: 28px;
}

.icon-user::before {
  background: url('@/assets/icons/user.svg') no-repeat center;
}

.icon-gift::before {
  background: url('@/assets/icons/gift.svg') no-repeat center;
}

.progress-bar-track {
  height: 8px;
  background-color: rgba(var(--v-theme-on-surface), 0.08);
}

.progress-bar-fill {
  transition: width 0.3s ease;
}
</style>
